import TokenLogo from '@/components/token-logo'
import { Button } from '@/components/ui/button'
import Timeline from '@/components/ui/timeline'
import { indexDTFAtom, indexDTFBrandAtom } from '@/state/dtf/atoms'
import { ROUTES } from '@/utils/constants'
import { Trans, useLingui } from '@lingui/react/macro'
import { useAtom, useAtomValue } from 'jotai'
import { Link } from 'react-router-dom'
import {
  isProposalConfirmedAtom,
  isProposalValidAtom,
  dtfSettingsProposalDataAtom,
} from '../atoms'
import DTFSettingsProposalChanges from './dtf-settings-proposal-changes'
import SubmitProposalButton from './submit-proposal-button'
import SimulateProposalCard from '@/views/index-dtf/governance/components/simulate-proposal-card'
import { chainIdAtom } from '@/state/atoms'
import { Address } from 'viem'
import { shouldBypassFormValidation } from '@/utils/form-validation'
import { useFormContext } from 'react-hook-form'
import { useHasRelevantFormErrors } from '../hooks/use-has-relevant-form-errors'

const ConfirmProposalButton = ({ canProceed }: { canProceed: boolean }) => {
  const { t } = useLingui()
  const [isProposalConfirmed, setIsProposalConfirmed] = useAtom(
    isProposalConfirmedAtom
  )

  const handleConfirm = () => {
    if (!isProposalConfirmed && !canProceed) return
    setIsProposalConfirmed(!isProposalConfirmed)
  }

  return (
    <Button
      className="w-full"
      disabled={!canProceed && !isProposalConfirmed}
      variant={isProposalConfirmed ? 'outline' : 'default'}
      onClick={handleConfirm}
    >
      {isProposalConfirmed ? t`Edit proposal` : t`Confirm & prepare proposal`}
    </Button>
  )
}

const ProposalInstructions = () => {
  const { t } = useLingui()
  const isValid = useAtomValue(isProposalValidAtom)
  const confirmed = useAtomValue(isProposalConfirmedAtom)
  const {
    formState: { errors },
  } = useFormContext()
  const hasRelevantFormErrors = useHasRelevantFormErrors(errors)
  const bypassFormValidation = shouldBypassFormValidation()

  const canProceed = isValid && (!hasRelevantFormErrors || bypassFormValidation)

  const timelineItems = [
    {
      title: t`Configure proposal`,
      isActive: !canProceed,
      isCompleted: canProceed,
    },
    {
      title: t`Finalize basket proposal`,
      children: <ConfirmProposalButton canProceed={canProceed} />,
      isActive: canProceed && !confirmed,
      isCompleted: confirmed,
    },
    {
      title: t`Review & describe your proposal`,
      children: <SubmitProposalButton />,
      isActive: confirmed,
    },
    {
      title: t`Voting delay begins`,
    },
  ]

  return (
    <div className="p-4 pr-10 ml-4 mb-4 w-full">
      <Timeline items={timelineItems} />
    </div>
  )
}

const Header = () => {
  const dtf = useAtomValue(indexDTFAtom)
  const brand = useAtomValue(indexDTFBrandAtom)

  return (
    <div className="flex items-center p-6 gap-2 bg-card rounded-t-3xl">
      <TokenLogo size="lg" src={brand?.dtf?.icon || undefined} />
      <h3 className="font-bold mr-auto">${dtf?.token.symbol}</h3>
      <Link to={`../${ROUTES.GOVERNANCE}`}>
        <Button
          variant="outline"
          size="xs"
          className="rounded-[42px] font-light text-destructive hover:text-destructive"
        >
          <Trans>Cancel</Trans>
        </Button>
      </Link>
    </div>
  )
}

const VaultProposalChangePreview = () => {
  const isProposalConfirmed = useAtomValue(isProposalConfirmedAtom)

  if (isProposalConfirmed) return null

  return (
    <div className="mt-2 border-4 border-secondary rounded-3xl bg-background p-2">
      <h3 className="font-bold mb-6 text-primary px-4 pt-4">
        <Trans>Proposed changes</Trans>
      </h3>
      <DTFSettingsProposalChanges />
    </div>
  )
}

const ProposalOverview = () => {
  return (
    <div className="border-4 border-secondary rounded-3xl bg-background">
      <Header />
      <ProposalInstructions />
    </div>
  )
}

const SimulateProposalSection = () => {
  const isProposalConfirmed = useAtomValue(isProposalConfirmedAtom)
  const proposalData = useAtomValue(dtfSettingsProposalDataAtom)
  const indexDTF = useAtomValue(indexDTFAtom)
  const chainId = useAtomValue(chainIdAtom)

  // Determine which governance to use (owner for DTF settings)
  const governorAddress = indexDTF?.ownerGovernance?.id as Address
  const timelockAddress = indexDTF?.ownerGovernance?.timelock?.id as Address
  const voteTokenAddress = indexDTF?.stToken?.id as Address

  // Construct simulation proposal data
  const simulationData = proposalData
    ? {
        targets: proposalData.targets,
        values: proposalData.calldatas.map(() => 0n), // DTF settings proposals have no value transfers
        calldatas: proposalData.calldatas,
      }
    : null

  return (
    <SimulateProposalCard
      isProposalConfirmed={isProposalConfirmed}
      proposalData={simulationData}
      governorAddress={governorAddress}
      timelockAddress={timelockAddress}
      voteTokenAddress={voteTokenAddress}
      chainId={chainId}
    />
  )
}

const DTFSettingsProposalOverview = () => {
  const isProposalConfirmed = useAtomValue(isProposalConfirmedAtom)

  return (
    <div className="flex flex-col gap-2 relative">
      <div className={!isProposalConfirmed ? "sticky top-0" : ""}>
        <ProposalOverview />
      </div>
      <VaultProposalChangePreview />
      <SimulateProposalSection />
    </div>
  )
}

export default DTFSettingsProposalOverview
